import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import {
  enrichLicense,
  licenseSearchText,
} from "../../hooks/licenseHelpers";
import { License, EnrichedLicense } from "../../store/types";
import {
  extendedLicenseColumns,
  getLicenseContextMenuItems,
  getLicenseMobileActions,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import { Container } from "../../components/Container/Container";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import type { DatatableHandle } from "../../components/CustomDatatable/CustomDatatable";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { IconPlus, IconSearch } from "../../assets/icons/Icon";
import {
  PageName,
  FlexLineJustify,
  FlexLineGapSmall,
  FilterComponents,
  Label,
  InputContainer,
  Tag,
} from "../../styles/PageLayoutStyles.styles";

const Licenses: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleAssignSeat,
    handleRemoveSeat,
    handleDeleteLicense,
    licenses,
    refetchData,
    toastRef,
  } = useLicensesAPI();
  const { users } = useUsersAPI();
  const { companies, companiesDropdownOpts } = useCompaniesAPI();
  const { projects, projectsDropdownOpts } = useProjectsAPI();
  const tableRef = useRef<DatatableHandle<License>>(null);
  const [tableData, setTableData] = useState<EnrichedLicense[] | null>(null);

  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<
    | "create"
    | "edit"
    | "delete"
    | "enable"
    | "disable"
    | "addUser"
    | "editUser"
    | "deleteUser"
  >("create");

  useEffect(() => {
    if (licenses) {
      setTableData(
        licenses.map((license: License) =>
          enrichLicense(license, { projects, companies })
        )
      );
    } else {
      setTableData(null);
    }
  }, [licenses, companies, projects]);

  const licenseMap = useMemo(() => {
    if (!tableData) return {};
    return tableData.reduce((acc, lic) => {
      acc[lic.id] = lic;
      return acc;
    }, {} as Record<string, EnrichedLicense>);
  }, [tableData]);

  const handleView = (license: License) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleCreate = () => {
    setSelectedLicense(null);

    if (isMobile) {
      navigate("/licenses/form?mode=create");
    } else {
      setDialogType("create");
      setDialogVisible(true);
    }
  };

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !tableData) return tableData;
    const lowerSearch = searchValue.toLowerCase();
    return tableData.filter((license) =>
      licenseSearchText(license).includes(lowerSearch)
    );
  }, [tableData, searchValue]);

  const handleEdit = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setDialogType("edit");
      setDialogVisible(true);
    }
  };

  const handleDelete = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setDialogType("delete");
      setDialogVisible(true);
    }
  };

  const handleEnable = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=enable&id=${license.id}`);
    } else {
      setDialogType("enable");
      setDialogVisible(true);
    }
  };

  const handleDisable = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=disable&id=${license.id}`);
    } else {
      setDialogType("disable");
      setDialogVisible(true);
    }
  };

  const handleAddUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=addUser&id=${license.id}`);
    } else {
      setDialogType("addUser");
      setDialogVisible(true);
    }
  };

  const handleEditLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=editUser&id=${license.id}`);
    } else {
      setDialogType("editUser");
      setDialogVisible(true);
    }
  };

  const deleteLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=deleteUser&id=${license.id}`);
    } else {
      setDialogType("deleteUser");
      setDialogVisible(true);
    }
  };

  const contextMenuItems = useMemo(
    () =>
      getLicenseContextMenuItems(
        selectedLicense,
        handleAddUser,
        handleEditLicenseUser,
        deleteLicenseUser,
        handleEnable,
        handleDisable
      ),
    [selectedLicense, handleAddUser, handleEnable, handleDisable]
  );

  const mobileActions = useMemo(
    () => getLicenseMobileActions(handleEnable, handleDisable),
    [handleEnable, handleDisable]
  );

  const handleDialogHide = () => {
    setDialogVisible(false);
  };

  const handleSelectionChange = (selected: License | null) => {
    setSelectedLicense(selected);
  };

  const resetAllFilters = () => {
    if (tableRef.current) {
      tableRef.current.reset();
    }
    setSearchValue("");
  };

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <FlexLineJustify>
        <PageName>
          <H2>Лицензии</H2>
          <Tag>
            {isMobile ? "" : "Количество:"}{" "}
            {licenses?.length.toString() ?? "..."}
          </Tag>
        </PageName>

        <FlexLineGapSmall>
          <Button onClick={handleCreate}>
            <IconPlus size={20} />
            <Label>Новая лицензия</Label>
          </Button>
          <FilterComponents>
            <Button
              icon="pi pi-filter-slash"
              onClick={resetAllFilters}
              outlined
            >
              <Label>Сбросить</Label>
            </Button>
            <InputContainer>
              <CustomInputText
                value={searchValue}
                setValue={setSearchValue}
                placeholder="Поиск..."
                icon={<IconSearch size={20} />}
              />
            </InputContainer>
          </FilterComponents>
        </FlexLineGapSmall>
      </FlexLineJustify>
      <CustomDatatable<License>
        ref={tableRef}
        data={filteredData ?? []}
        columns={extendedLicenseColumns}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRowDoubleClick={handleView}
        selected={selectedLicense}
        onSelectionChange={handleSelectionChange}
        getRowId={(license) => license.id}
        extraContextMenuItems={contextMenuItems}
        mobileActions={mobileActions}
        sortable
      />
      <LicenseDialogs
        visible={dialogVisible}
        onHide={handleDialogHide}
        dialogType={dialogType}
        license={selectedLicense!}
        enrichedLicense={
          selectedLicense ? licenseMap[selectedLicense.id] : undefined
        }
        handleActivateLicense={handleActivateLicense}
        handleDeactivateLicense={handleDeactivateLicense}
        handleAddLicense={handlePostLicense}
        handleEditLicense={handleUpdateLicense}
        handleAssignSeat={handleAssignSeat}
        handleRemoveSeat={handleRemoveSeat}
        handleDeleteLicense={handleDeleteLicense}
        refetchData={refetchData}
        companiesOptions={companiesDropdownOpts}
        usersList={users}
        projectsOptions={projectsDropdownOpts}
        licensesEP={licensesEP}
      />
    </Container>
  );
};

export default Licenses;
